/**
 * SoundManager — Web Audio API singleton. AudioContext + master gain + localStorage persistence.
 *
 * Created once via initAudio() after first user gesture (splash click).
 * All synth sounds connect to the master gain node.
 */

const STORAGE_KEY = 'geometry-blast-audio';

interface AudioSettings {
  masterVolume: number;
  muted: boolean;
}

function loadSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as AudioSettings;
  } catch { /* ignore */ }
  return { masterVolume: 0.5, muted: false };
}

function saveSettings(settings: AudioSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch { /* ignore */ }
}

// ─── Singleton State ────────────────────────────────────────────────────────

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let settings: AudioSettings = loadSettings();

/** Initialize the AudioContext. Call after first user gesture (click/key). */
export function initAudio(): void {
  if (ctx) return;
  try {
    ctx = new AudioContext();
    masterGain = ctx.createGain();
    masterGain.gain.value = settings.muted ? 0 : settings.masterVolume;
    masterGain.connect(ctx.destination);
  } catch (e) {
    console.warn('[Audio] Failed to create AudioContext:', e);
  }
}

/** Get the AudioContext (null before init). */
export function getAudioContext(): AudioContext | null {
  return ctx;
}

/** Get the master gain node (null before init). Synth sounds connect here. */
export function getMasterGain(): GainNode | null {
  return masterGain;
}

/** Set master volume (0–1). Persists to localStorage. */
export function setVolume(v: number): void {
  settings.masterVolume = Math.max(0, Math.min(1, v));
  if (masterGain && !settings.muted) {
    masterGain.gain.value = settings.masterVolume;
  }
  saveSettings(settings);
}

/** Get current master volume (0–1). */
export function getVolume(): number {
  return settings.masterVolume;
}

/** Toggle mute on/off. Persists to localStorage. */
export function toggleMute(): void {
  settings.muted = !settings.muted;
  if (masterGain) {
    masterGain.gain.value = settings.muted ? 0 : settings.masterVolume;
  }
  saveSettings(settings);
}

/** Check if muted. */
export function isMuted(): boolean {
  return settings.muted;
}
